/**
 * Does releasing a recall return it to the pool, or end it permanently?
 *
 * THE RULE: recycle when the fact is about *this attempt*; final when the fact is about
 * the *vehicle or the recall*. Test — would trying again tomorrow, or with a different
 * technician, plausibly succeed? If yes, it recycles.
 *
 * Why this file exists
 * --------------------
 * The disposition was decided by matching display strings, case-sensitively, against a
 * list declared inside a function. Three clients wrote three different spellings:
 *
 *   web  AUTO_UNASSIGN_REASONS  'Parts not available'   'Time constraints'
 *   app  OTHER_REASONS          'Parts Not Available'   'Time Constraints'
 *   web  COMMON_REASONS (V3)    'Parts not available'   'Time constraints'
 *
 * So a technician releasing "Parts Not Available" from the phone had it permanently
 * cancelled instead of returned to the pool. Worse, the app's RecallWorkScreen appends
 * mandatory notes to the reason before classifying, producing strings like
 * 'Parts Not Available: need parts' that match nothing at all. Production holds exactly
 * that row, and 'Time Constraints: I was not able to finish' — both meant to come back.
 *
 * Of ~535 release reasons in production, only 2 would recycle under the old matcher.
 *
 * The fix is deliberately tolerant on READ. There is no minimum-version enforcement and
 * OTA updates are appVersion-scoped, so the fleet cannot be force-updated: normalising
 * here repairs every app build already in the field without shipping an app release.
 */

export type ReleaseDisposition = 'recycle' | 'closeout';

export type ReleaseReasonCode =
  | 'time_constraints'
  | 'parts_unavailable'
  | 'higher_tech_level'
  | 'customer_declined'
  | 'vehicle_condition'
  | 'already_completed_elsewhere';

interface ReleaseReasonDefinition {
  code: ReleaseReasonCode;
  /** Canonical label. This is what new UI renders and what we persist for humans. */
  label: string;
  disposition: ReleaseDisposition;
  /**
   * Every spelling any client has ever written, so an old build keeps working.
   * Matched after normalisation, so case and surrounding space do not matter here.
   */
  aliases: string[];
}

export const RELEASE_REASONS: readonly ReleaseReasonDefinition[] = [
  // --- RECYCLE: nothing is wrong with the vehicle, only with this attempt ---
  {
    code: 'time_constraints',
    label: 'Time constraints',
    disposition: 'recycle',
    aliases: ['time constraints']
  },
  {
    code: 'parts_unavailable',
    label: 'Parts not available',
    disposition: 'recycle',
    aliases: ['parts not available']
  },
  {
    code: 'higher_tech_level',
    label: 'Higher Tech Level Needed',
    disposition: 'recycle',
    aliases: ['higher tech level needed']
  },

  // --- CLOSEOUT: the fact is about the vehicle, the recall, or the dealer ---
  {
    code: 'customer_declined',
    label: 'Customer declined service',
    disposition: 'closeout',
    // The "customer" is the dealership. A dealer who refuses access is unlikely to let
    // the technician touch any car on their lot — a dealer-level signal, not a car-level
    // one. 263 rows across 131 dealers, but 10 dealers account for 65 of them.
    aliases: ['customer declined service']
  },
  {
    code: 'vehicle_condition',
    label: 'Vehicle condition prevents service',
    disposition: 'closeout',
    aliases: ['vehicle condition prevents service']
  },
  {
    code: 'already_completed_elsewhere',
    label: 'Recall already completed',
    disposition: 'closeout',
    // Someone else did the work. Not `completed` — that is reserved for work performed
    // by us, and 0 of the 99 such rows in production have any labour recorded against
    // them. Filing them as completed would credit us with 99 jobs we did not do.
    // It is also a stale-data signal: our source said open, reality said closed.
    aliases: ['recall already completed', 'vehicle already serviced elsewhere']
  }
] as const;

/** What an unrecognised reason falls back to. Conservative: never invent a recycle. */
export const UNKNOWN_REASON_DISPOSITION: ReleaseDisposition = 'closeout';

const REASON_BY_ALIAS: ReadonlyMap<string, ReleaseReasonDefinition> = new Map(
  RELEASE_REASONS.flatMap(definition =>
    definition.aliases.map(alias => [alias, definition] as const)
  )
);

/**
 * Strip a free-text note that a client appended to the reason.
 *
 * The app's RecallWorkScreen builds `${reason}: ${notes}` and its recycle path builds
 * `${reason} — ${notes}`, then hands the whole string to the classifier. We only cut at
 * a genuine separator and then require an exact match on what remains, so an unrelated
 * sentence that merely starts with a known phrase is NOT silently treated as that reason.
 */
function stripAppendedNote(reason: string): string {
  const separators = [' — ', ' - ', ': '];
  let candidate = reason;
  for (const separator of separators) {
    const index = candidate.indexOf(separator);
    if (index > 0) {
      candidate = candidate.slice(0, index);
    }
  }
  return candidate;
}

function normalise(reason: string): string {
  return stripAppendedNote(reason.trim()).trim().toLowerCase();
}

/** Resolve a reason string written by any client, past or present. */
export function resolveReleaseReason(
  reason: string | null | undefined
): ReleaseReasonDefinition | null {
  if (!reason) return null;
  return REASON_BY_ALIAS.get(normalise(reason)) ?? null;
}

export interface ReleaseClassification {
  disposition: ReleaseDisposition;
  /** null when the reason was free text we do not recognise. */
  code: ReleaseReasonCode | null;
  /** True when a reason was supplied but matched nothing — worth surfacing. */
  unrecognisedReason: boolean;
}

/**
 * The single place that decides whether a release returns a recall to the pool.
 *
 * Status drives it first: `incomplete` and `not_available` are always about the attempt.
 * Only `other` consults the reason.
 *
 * `not_there` is intentionally NOT treated as recycle here even though it means the same
 * thing as `not_available` — 218 of the 269 rows carrying it already sit at `cancelled`,
 * so changing how it reads would revive work dealers have stopped expecting. The write
 * side stops producing it separately; the read side is a data decision, not a code one.
 */
export function classifyRelease(status: string, reason?: string | null): ReleaseClassification {
  if (status === 'incomplete' || status === 'not_available') {
    return { disposition: 'recycle', code: null, unrecognisedReason: false };
  }

  if (status !== 'other') {
    return { disposition: 'closeout', code: null, unrecognisedReason: false };
  }

  const resolved = resolveReleaseReason(reason);
  if (!resolved) {
    return {
      disposition: UNKNOWN_REASON_DISPOSITION,
      code: null,
      unrecognisedReason: Boolean(reason?.trim())
    };
  }

  return {
    disposition: resolved.disposition,
    code: resolved.code,
    unrecognisedReason: false
  };
}

/** Canonical labels for a picker, in the order the audit lists them. */
export const RELEASE_REASON_LABELS: readonly string[] = RELEASE_REASONS.map(r => r.label);

/** Labels whose reasons return the recall to the pool, for UI that wants to say so. */
export const RECYCLING_REASON_LABELS: readonly string[] = RELEASE_REASONS.filter(
  r => r.disposition === 'recycle'
).map(r => r.label);

export function isRecyclingReason(reason: string | null | undefined): boolean {
  return resolveReleaseReason(reason)?.disposition === 'recycle';
}

/**
 * A status and reason that contradict each other, e.g. `vehicle_sold` with
 * "Time constraints" — production holds 2 such rows. Reasons only qualify `other`.
 */
export function isReasonCompatibleWithStatus(status: string, reason?: string | null): boolean {
  if (!reason?.trim()) return true;
  return status === 'other';
}

// ── The removal dialog ──────────────────────────────────────────────────────
//
// RemoveRecallDialog offers three choices, and the recall list and the recall
// map each mapped them to a status independently. They disagreed: the list sent
// `not_available` (which recycles) while the map sent `not_there` (which closes
// out permanently) — the same dialog, the same click, opposite outcomes, and 277
// rows in production carry the map's value. They disagreed on the reason text
// too: "Vehicle not available" against "Vehicle not there".
//
// The duplication was the root cause, so the mapping lives here once. `not_there`
// is no longer emitted; historical rows keep it, and how to read them is a
// backend decision (B5).

/** The three choices RemoveRecallDialog offers. */
export type RemovalReason = 'vehicle_not_there' | 'completed' | 'other';

/** What those choices mean to the status APIs. */
export type RemovalStatus = 'completed' | 'not_available' | 'other';

const REMOVAL_OUTCOMES: Record<RemovalReason, { status: RemovalStatus; label: string }> = {
  vehicle_not_there: { status: 'not_available', label: 'Vehicle not available' },
  completed: { status: 'completed', label: 'Completed' },
  other: { status: 'other', label: 'Other reason' }
};

export function statusForRemovalReason(reason: RemovalReason): RemovalStatus {
  return REMOVAL_OUTCOMES[reason].status;
}

/**
 * The reason text to record. Anything the user typed wins; otherwise the label
 * of the choice they made. `other` with nothing typed yields undefined, which
 * both pages already relied on.
 */
export function reasonTextForRemoval(
  reason: RemovalReason,
  otherReason?: string
): string | undefined {
  if (otherReason) return otherReason;
  return reason === 'other' ? undefined : REMOVAL_OUTCOMES[reason].label;
}
